import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import DebugDrawer from './components/common/DebugDrawer';
import { store } from './store';
import { useAppSelector, useAppDispatch } from './hooks/redux';
import { Capacitor } from '@capacitor/core';
import { backButtonManager } from './utils/backButtonManager';
// Removed PushNotifications to fix Android boot crash securely.
import { fetchMyApplications } from './store/slices/opportunitiesSlice';
import { fetchCurrentUser } from './store/slices/authSlice';
import { Analytics } from "@vercel/analytics/react"
import api from './services/api';
// Pages
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import ProfileCompletePage from './pages/ProfileCompletePage';
import ExplorePage from './pages/ExplorePage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import NotificationsPage from './pages/NotificationsPage';
import OpportunityDetailsPage from './pages/OpportunityDetailsPage';
import CreatePostPage from './pages/CreatePostPage';
import useWebSocket from './hooks/useWebSocket';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DataDeletionStatusPage from './pages/DataDeletionStatusPage';
// [Subscription removed] PricingPage is disabled — platform is fully free.
// import PricingPage from './pages/PricingPage';
import FactsPage from './pages/FactsPage';
import CRMPage from './pages/CRMPage';
import ProjectGalleryPage from './pages/ProjectGalleryPage';
import InvoicePage from './pages/InvoicePage';
import ContractPage from './pages/ContractPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ConfirmedPage from './pages/ConfirmedPage';
import DeclinedPage from './pages/DeclinedPage';
import CRMLeadRespondPage from './pages/CRMLeadRespondPage';
// [Subscription removed] CheckoutPage is disabled — platform is fully free.
// import CheckoutPage from './pages/CheckoutPage';


// Layout
import Layout from './components/layout/ModernLayout'; // Using ModernLayout as the main Layout
import LoadingSpinner from './components/common/LoadingSpinner';
import OpportunitiesPage from './pages/OpportunitiesPage';
import PostOpportunityPage from './pages/PostOpportunityPage';
import MyOpportunitiesPage from './pages/MyOpportunitiesPage';
import ReviewApplicantsPage from './pages/ReviewApplicantsPage';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ConnectionStatus from './components/common/ConnectionStatus';
import EditOpportunityPage from './pages/EditOpportunityPage';
import DashboardPerformancePage from './pages/DashboardPerformancePage';

import CollaborationRequestsPage from './pages/CollaborationRequestsPage';
import CollaborationNotifications from './components/navigation/CollaborationNotifications';
import MemoryBoard from './components/dashboard/MemoryBoard';
import MemoryBoard2 from './components/dashboard/MemoryBoard2';
import SettingsPage from './pages/SettingsPage';
import CollaborationDetailsPage from './pages/CollaborationDetailsPage';
// [Subscription removed] SubscriptionInvoicePage is disabled — platform is fully free.
// import SubscriptionInvoicePage from './pages/SubscriptionInvoicePage';
import { ThemeProvider } from './context/ThemeContext';
// [Subscription removed] PlanStatusModal is disabled — platform is fully free.
// import PlanStatusModal from './components/dashboard/PlanStatusModal';
import { pruneImageCache } from './utils/imageCache';
import UploadWidget from './components/common/UploadWidget';
import SpecialtyUpgradeModal from './components/common/SpecialtyUpgradeModal';

import DisabledAccountBanner from './components/common/DisabledAccountBanner';
import OperationalRadiusModal from './components/common/OperationalRadiusModal';
import TermsAcceptanceModal from './components/common/TermsAcceptanceModal';
import { useAutoGeolocation, isUserAccountBangalore } from './hooks/useAutoGeolocation';

// Profile page wrapper to ensure clean instance when switching between users
const ProfilePageWrapper: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  return <ProfilePage key={id || 'my-profile'} />;
};

function AppContent() {
  const { isAuthenticated, loading, user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { coords, isOutsideRadius, setIsOutsideRadius, permissionState, requestLocation } = useAutoGeolocation();


  // Handle native safe area fallback on Capacitor platform
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const currentTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top');
      if (!currentTop || currentTop.trim() === '' || currentTop.trim() === '0px') {
        document.documentElement.style.setProperty('--safe-area-inset-top', '32px');
      }
    }
  }, []);

  // Handle Android back button
  useEffect(() => {
    let backListener: any;
    
    const setupBackListener = async () => {
      if (Capacitor.isNativePlatform()) {
        const { App } = await import('@capacitor/app');
        backListener = await App.addListener('backButton', ({ canGoBack }) => {
          // If any modal/drawer is open and handles the back action, do not change page
          if (backButtonManager.handleBack()) {
            return;
          }

          if (location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/login') {
            App.exitApp();
          } else {
            navigate(-1);
          }
        });
      }
    };

    setupBackListener();

    return () => {
      if (backListener) {
        backListener.remove();
      }
    };
  }, [location.pathname, navigate]);

  // Handle post-login plan redirection
  useEffect(() => {
    pruneImageCache(); // Clean up expired image signatures
    if (isAuthenticated && user) {
      // [Subscription removed] pendingPlan checkout flow removed — platform is fully free.
      // const pendingPlanStr = localStorage.getItem('pendingPlan');
      // if (pendingPlanStr) {
      //   try {
      //     const pendingPlan = JSON.parse(pendingPlanStr);
      //     if (pendingPlan && pendingPlan.name) {
      //       localStorage.removeItem('pendingPlan');
      //
      //       // Check if user is already on this plan
      //       const currentTier = user.subscription_tier?.toLowerCase() || 'basic';
      //       const targetTier = pendingPlan.name.toLowerCase();
      //
      //       if (currentTier === targetTier || (currentTier === 'free' && targetTier === 'basic')) {
      //         setStatusModal({ isOpen: true, planName: pendingPlan.name });
      //       } else {
      //         // Navigate to checkout with the pending plan details
      //         navigate(`/checkout?plan=${pendingPlan.name}&cycle=${pendingPlan.billingCycle || 'monthly'}`);
      //       }
      //     }
      //   } catch (e) {
      //     console.error("Failed to parse pendingPlan:", e);
      //     localStorage.removeItem('pendingPlan');
      //   }
      // }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (user?.id) {
      // console.log('🔄 Global Effect: Syncing user profile and fetching applications for user:', user.id);
      dispatch(fetchCurrentUser(user.id));
      dispatch(fetchMyApplications());
    }
  }, [dispatch, user?.id]);

  // Global Image Protection
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'IMG') {
        e.preventDefault();
      }
    };

    const handleDragStart = (e: DragEvent) => {
      if ((e.target as HTMLElement).tagName === 'IMG') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);
  // Native Push Notifications setup for Capacitor
  useEffect(() => {
    let pushListener1: any;
    let pushListener2: any;
    let pushListener3: any;
    let pushListener4: any;

    const setupPush = async () => {
      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        
        try {
          const permission = await PushNotifications.requestPermissions();
          if (permission.receive === 'granted') {
            await PushNotifications.register();
          }

          pushListener1 = await PushNotifications.addListener('registration', (token) => {
            console.log('Push token:', token.value);
            api.post('/users/save-push-token', { fcm_token: token.value })
              .then(() => console.log('FCM token saved successfully'))
              .catch(err => console.error('FCM save failed:', err));
          });

          pushListener2 = await PushNotifications.addListener('registrationError', (error) => {
            console.error('Push error:', error);
          });

          pushListener3 = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            if (notification.body) {
              toast.success(notification.body); 
            }
          });

          pushListener4 = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            const data = notification.notification.data;
            if (data && data.payload) {
              try {
                const payload = JSON.parse(data.payload);
                const action = payload.action_type;
                const refId = payload.reference_id;
                
                switch (action) {
                  case 'open_chat':
                  case 'new_message':
                    navigate('/messages', { state: { openConversationId: refId, fromNotification: true } });
                    break;
                  case 'view_profile':
                    navigate(`/profile/${refId}`);
                    break;
                  case 'view_collaboration':
                    navigate(`/collaborations/${refId}`);
                    break;
                  case 'view_opportunity':
                    navigate(`/opportunities/${refId}`);
                    break;
                  case 'collab_request':
                  case 'view_request':
                    navigate('/collaboration-requests', { state: { selectedRequestId: refId } });
                    break;
                  default:
                    if (payload.deep_link) {
                      navigate(payload.deep_link.replace('clickcollab://app', ''));
                    }
                }
              } catch (e) {
                console.error('Failed to parse push data payload:', e);
              }
            }
          });
        } catch (e) {
          console.error('Push setup failed:', e);
        }
      }
    };

    setupPush();

    return () => {
      if (pushListener1) pushListener1.remove();
      if (pushListener2) pushListener2.remove();
      if (pushListener3) pushListener3.remove();
      if (pushListener4) pushListener4.remove();
    };
  }, [user?.id]);


  const getDefaultRedirect = () => {
    if (user && !user.user_type) return '/profile/complete';
    if (user?.is_admin) return '/admin';
    if (user?.user_type === 'hirer') {
      return '/memory-board';
    }
    return '/dashboard';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const isNative = Capacitor.isNativePlatform();

  // Redirect users who have not completed role/location setup to /profile/complete
  if (isAuthenticated && user && !user.user_type && location.pathname !== '/profile/complete') {
    return <Navigate to="/profile/complete" replace />;
  }

  // On native mobile apps, disallow all non-login/guest screens:
  // If user is logged out or not logged in yet, only login/signup (and compliance) screens are accessible.
  const isAuthOrCompliancePath = (pathname: string) => {
    return (
      pathname === '/login' ||
      pathname === '/register' ||
      pathname === '/early-access' ||
      pathname === '/forgot-password' ||
      pathname === '/reset-password' ||
      pathname === '/verify-email' ||
      pathname === '/privacy-policy' ||
      pathname === '/contact' ||
      pathname === '/data-deletion-status'
    );
  };

  if (isNative && !isAuthenticated && !isAuthOrCompliancePath(location.pathname)) {
    // Save target path (e.g. shared profile, explore, or opportunities link)
    // so the user is seamlessly redirected to it immediately after signing in.
    if (location.pathname !== '/' && location.pathname !== '/home') {
      localStorage.setItem('redirect_after_login', location.pathname + location.search);
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <WebSocketProvider>
      <div className="App">
      <ConnectionStatus /> {/* Add this */}
      <UploadWidget /> {/* Add this */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#374151',
              boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1)',
              borderRadius: '0.75rem',
              maxWidth: '500px',
            },
          }}
        >
          {(t) => (
            <ToastBar toast={t} style={{ ...t.style, maxWidth: '500px' }}>
              {({ icon, message }) => (
                <div
                  className="flex items-center gap-3 cursor-pointer w-full"
                  onClick={() => toast.dismiss(t.id)}
                >
                  {icon}
                  <div className="flex-1 text-sm font-medium whitespace-nowrap">{message}</div>
                  {t.type !== 'loading' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.dismiss(t.id);
                      }}
                      className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                      aria-label="Close"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </ToastBar>
          )}
        </Toaster>
        
        <Routes>
          <Route 
            path="/home" 
            element={isNative ? (!isAuthenticated ? <Navigate to="/login" replace /> : <Navigate to={getDefaultRedirect()} replace />) : <LandingPage />} 
          />
          <Route 
            path="/" 
            element={!isAuthenticated ? (isNative ? <Navigate to="/login" replace /> : <LandingPage />) : <Navigate to={getDefaultRedirect()} />} 
          />
          <Route 
            path="/login" 
            element={!isAuthenticated ? <LoginPage /> : <Navigate to={getDefaultRedirect()} />} 
          />
          <Route 
            path="/early-access" 
            element={<LoginPage />} 
          />
          <Route 
            path="/register" 
            element={!isAuthenticated ? <RegisterPage /> : <Navigate to={getDefaultRedirect()} />} 
          />
          <Route 
            path="/verify-email" 
            element={<VerifyEmailPage />} 
          />
          <Route 
            path="/forgot-password" 
            element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to={getDefaultRedirect()} />} 
          />
          <Route 
            path="/reset-password" 
            element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to={getDefaultRedirect()} />} 
          />
          <Route 
            path="/contact" 
            element={<ContactPage />} 
          />
          <Route 
            path="/privacy-policy" 
            element={<PrivacyPolicyPage />} 
          />
          <Route 
            path="/data-deletion-status" 
            element={<DataDeletionStatusPage />} 
          />
          {/*<Route path="/pricing" element={<PricingPage />} />*/}
			<Route 
				path="/gallery/:projectId" 
				element={<ProjectGalleryPage />} 
			/>
			<Route 
				path="/invoice/:invoiceId" 
				element={<InvoicePage />} 
			/>
			<Route
				path="/crm/contract/:contractId"
				element={<ContractPage />}
			/>
			<Route
				path="/crm/leads/confirmed"
				element={<ConfirmedPage />}
			/>
			<Route
				path="/crm/leads/declined"
				element={<DeclinedPage />}
			/>
			<Route
				path="/crm/leads/respond/:id"
				element={<CRMLeadRespondPage />}
			/>
			
			<Route path="/opportunities/post" element={isAuthenticated ? <Layout><PostOpportunityPage /></Layout> : <Navigate to="/login" />} />
          <Route path="/opportunities/my" element={isAuthenticated ? <Layout><MyOpportunitiesPage /></Layout> : <Navigate to="/login" />} />
          <Route path="/opportunities/:opportunityId/applications" element={isAuthenticated ? <Layout><ReviewApplicantsPage /></Layout> : <Navigate to="/login" />} />
          <Route path="/opportunities/:id/edit" element={isAuthenticated ? <Layout><EditOpportunityPage /></Layout> : <Navigate to="/login" />} />

          {/* Public Explore Routes - SEO Friendly on Web, Auth-gated on Mobile */}
          <Route 
            path="/explore/:city/:category" 
            element={isAuthenticated ? <Layout><ExplorePage /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ExplorePage />)} 
          />
          <Route 
            path="/explore/:city" 
            element={isAuthenticated ? <Layout><ExplorePage /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ExplorePage />)} 
          />
          <Route 
            path="/explore" 
            element={isAuthenticated ? <Layout><ExplorePage /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ExplorePage />)} 
          />

          <Route
            path="/opportunities/*"
            element={
              isAuthenticated ? (
                <Layout>
                  <Routes>
                    <Route path="/" element={<OpportunitiesPage />} />
                    <Route path="/:id" element={<OpportunityDetailsPage />} />
                  </Routes>
                </Layout>
              ) : isNative ? (
                <Navigate to="/login" replace />
              ) : (
                <Routes>
                  <Route path="/" element={<OpportunitiesPage />} />
                  <Route path="/:id" element={<OpportunityDetailsPage />} />
                </Routes>
              )
            }
          />

          <Route 
            path="/profile/complete" 
            element={isAuthenticated ? <ProfileCompletePage /> : <Navigate to="/login" />} 
          />

          <Route 
            path="/profile/edit" 
            element={isAuthenticated ? <Layout><EditProfilePage /></Layout> : <Navigate to="/login" />} 
          />
          {/* Profile routes - conditional layout for guests on Web, Auth-gated on Mobile */}
          <Route path="/profile" element={isAuthenticated ? <Layout><ProfilePageWrapper /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ProfilePageWrapper />)} />
          <Route path="/profile/:id" element={isAuthenticated ? <Layout><ProfilePageWrapper /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ProfilePageWrapper />)} />
          <Route path="/profile/:id/:active_tab" element={isAuthenticated ? <Layout><ProfilePageWrapper /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ProfilePageWrapper />)} />
          <Route path="/profile/:id/portfolio/folders/:folder_id" element={isAuthenticated ? <Layout><ProfilePageWrapper /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ProfilePageWrapper />)} />
          <Route path="/profile/:id/:active_tab/:item_id" element={isAuthenticated ? <Layout><ProfilePageWrapper /></Layout> : (isNative ? <Navigate to="/login" replace /> : <ProfilePageWrapper />)} />

          <Route 
            path="/*" 
            element={isAuthenticated ? (
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to={getDefaultRedirect()} />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/explore" element={<ExplorePage />} />
                  <Route path="/insights" element={<FactsPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/chat/:conversationId" element={<ChatPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/:tab" element={<SettingsPage />} />
                  {/* [Subscription removed] SubscriptionInvoicePage route removed */}
                  {/* <Route path="/settings/subscription-invoice/:id" element={<SubscriptionInvoicePage />} /> */}
                  <Route path="/opportunity/:id" element={<OpportunityDetailsPage />} />
                  <Route path="/create" element={<CreatePostPage />} />
                  <Route path="/dashboard/performance" element={<DashboardPerformancePage />} />
                  <Route path="/collaboration-requests" element={<CollaborationRequestsPage />} />
                  <Route path="/memory-board" element={<MemoryBoard />} />
                  <Route path="/collaborations/:id" element={<CollaborationDetailsPage />} />
                  <Route path="/crm" element={user?.user_type === 'hirer' ? <Navigate to={getDefaultRedirect()} /> : <CRMPage />} />
                  {/* [Subscription removed] Checkout page — redirect to home */}
                  {/*<Route path="/checkout" element={<CheckoutPage />} />*/}
                  {user?.is_admin && <Route path="/admin" element={<AdminDashboardPage />} />}
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/login" />
            )} 
          />
         
        </Routes>

        {/* [Subscription removed] PlanStatusModal removed */}
        {/*<PlanStatusModal */}
        {/*    isOpen={statusModal.isOpen} */}
        {/*    onClose={() => setStatusModal({ ...statusModal, isOpen: false })} */}
        {/*    planName={statusModal.planName} */}
        {/*/>*/}

        {/* Operational Radius Restriction Modal (Global) */}
        <OperationalRadiusModal
          isOpen={isOutsideRadius && !isUserAccountBangalore(user)}
          onClose={() => setIsOutsideRadius(false)}
          initialEmail={user?.email || ''}
          initialLatitude={coords?.latitude}
          initialLongitude={coords?.longitude}
          permissionState={permissionState}
          onRequestLocation={requestLocation}
        />

        {/* Force creators with empty specialty to select one */}
        <SpecialtyUpgradeModal 
          isOpen={
            isAuthenticated && 
            !!user && 
            user.user_type === 'creator' && 
            !user.creator_sub_type
          } 
        />

        {/* Blocking Terms Acceptance Modal for accounts that have not accepted terms yet */}
        <TermsAcceptanceModal 
          isOpen={isAuthenticated && !!user && !user.agreed_terms} 
        />
      </div>
    </WebSocketProvider>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AppContent />
        <DebugDrawer />
        {/* {window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && <Analytics />} */}
      </ThemeProvider>
    </Router>
  );
}

export default App;
